import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Table from "../shared/table/Table";
import { FiEdit3, FiTrash2 } from "react-icons/fi";
import useUserLanguage from "../../hooks/useUserLanguage";
import useTranslations from "../../hooks/useTranslations";
import usePermission from "../../hooks/usePermission";
import { toast, ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faListUl } from "@fortawesome/free-solid-svg-icons";
import "react-toastify/dist/ReactToastify.css";
import getIcon from "../../utils/getIcon";
import { confirmAlert } from "react-confirm-alert"; 

const ModuleTable = () => {
  const [modules, setModules] = useState([]);
  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const { canEdit, canDelete } = usePermission("static_developer_settings");
  const getText = (key, defaultValue = "") => translations[key] || defaultValue;

  const isTranslationsReady = Object.keys(translations).length > 0;

  const fetchModules = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/modules`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setModules(response.data);
    } catch (error) {
      toast.error("Modül listesi alınamadı!");
    }
  };

  const handleSoftDelete = (id) => {
    confirmAlert({
      title: getText("SOFT_DELETE_CONFIRM_TITLE"),
      message: getText("SOFT_DELETE_CONFIRM_MSG"),
      buttons: [
        {
          label: getText("YES_SOFT_DELETE"),
          onClick: async () => {
            try {
              const token = localStorage.getItem("admin_token");
              await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/modules/soft-delete/${id}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
              });
              toast.success(getText("SUCCESS_MODULE_SOFT_DELETED"));
              fetchModules();
            } catch (err) {
              console.error("Soft-delete hatası:", err);
              toast.error(getText("ERROR_MODULE_SOFT_DELETE_FAILED"));
            }
          },
        },
        {
          label: getText("CANCEL"),
          onClick: () => toast.info(getText("SOFT_DELETE_CANCELLED")),
        },
      ],
    });
  };

  useEffect(() => {
    fetchModules();
  }, []);

  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: getText("TABLE_ID_LABEL"),
        meta: { headerClassName: "text-start", className: "text-start" },
        size: 60,
      },
      {
        accessorKey: "icon",
        header: getText("TABLE_ICON_LABEL"),
        meta: { headerClassName: "text-start", className: "text-start" },
        size: 60,
        cell: (info) => {
          let iconValue = info.getValue();
          if (iconValue && !iconValue.startsWith("/") && iconValue.endsWith(".svg")) {
            iconValue = `/icons/svg/${iconValue}`;
          }
          return getIcon(iconValue);
        },
      },
      
      {
        accessorKey: "name",
        header: getText("TABLE_MODULE_NAME_LABEL"),
        meta: { headerClassName: "text-start", className: "text-start" },
        size: 200,
      },
      {
        accessorKey: "created_at",
        header: getText("TABLE_CREATED_AT"),
        size: 130,
        meta: {
          headerClassName: "text-end",
          className: "text-end",
        },
        cell: (info) => {
          const raw = info.getValue();
          return new Date(raw).toLocaleDateString("tr-TR");
        },
      },
      {
        accessorKey: "updated_at",
        header: getText("TABLE_UPDATED_AT"),
        size: 130,
        meta: {
          headerClassName: "text-end",
          className: "text-end",
        },
        cell: (info) => {
          const raw = info.getValue();
          return raw ? new Date(raw).toLocaleDateString("tr-TR") : "-";
        },
      },
      {
        accessorKey: "actions",
        header: getText("TABLE_ACTIONS_LABEL"),
        enableSorting: false,
        meta: { headerClassName: "text-end", className: "text-end" },
        size: 120,
        cell: (info) => {
          const row = info.row.original;
          const hasDeps = row.hasDependencies;
          return (
            <div className="hstack gap-2 justify-content-end">
                 {(row.category == 1 || row.category === true || row.category === "1") && (
                  <Link
                    to={`/modules/${row.multiple_slug}/category/list`}
                    className="avatar-text avatar-md text-warning bg-soft-warning border-soft-warning"
                    title={getText("CATEGORY")}
                  >
                    <FontAwesomeIcon icon={faListUl} />
                  </Link>
                )}
              {(
                <Link
                to={`/admin/modules/edit/${row.multiple_slug}`}
                className="avatar-text avatar-md text-success bg-soft-success border-soft-success"
                title={getText("EDIT")}
              >
                <FiEdit3 />
              </Link>
              )}
           
           {canDelete && (
            <div className="hstack gap-2 justify-content-end">
              {hasDeps ? (
                <button
                  className="avatar-text avatar-md text-secondary bg-soft-secondary border-soft-secondary"
                  title={getText("CANNOT_DELETE_HAS_DEPENDENCIES")}
                  disabled
                >
                  <FiTrash2 />
                </button>
              ) : (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handleSoftDelete(row.id);
                  }}
                  title={getText("DELETE")}
                  className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
                >
                  <FiTrash2 />
                </button>
              )}
            </div>
          )}

            </div>
          );
        },
      },
    ],
    [translations]
  );

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/modules/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Modül silindi!");
      fetchModules();
    } catch (error) {
      toast.error("Silme başarısız oldu!");
    }
  };

  if (!isTranslationsReady) return null;

  return (
    <>
      <style>{`
        /* /web klasöründeki CSS'leri bu sayfa için override et */
        #module-table-root {
          width: 100% !important;
          max-width: 100% !important;
          min-width: auto !important;
          margin: 0 !important;
          padding: 0 !important;
          background: transparent !important;
          position: relative !important;
          font-size: 14px !important;
          line-height: 1.5 !important;
        }
        
        #module-table-root * {
          box-sizing: border-box !important;
        }
        
        #module-table-root .container {
          width: 100% !important;
          max-width: 100% !important;
          min-width: auto !important;
          padding-right: 15px !important;
          padding-left: 15px !important;
          margin-right: auto !important;
          margin-left: auto !important;
        }
        
        #module-table-root table {
          width: 100% !important;
          max-width: 100% !important;
          min-width: auto !important;
          table-layout: auto !important;
        }
        
        #module-table-root img {
          max-width: 100% !important;
          height: auto !important;
          display: inline-block !important;
        }
        
        /* /web'den gelen background image'ları kaldır */
        #module-table-root::before,
        #module-table-root::after {
          display: none !important;
          content: none !important;
          background-image: none !important;
        }
        
        #module-table-root *::before,
        #module-table-root *::after {
          background-image: none !important;
        }
        
        /* "We will be at your service soon" gibi text'leri gizle */
        #module-table-root div:not([class*="table"]):not([class*="container"]):not([class*="stack"]):not([class*="avatar"]) {
          font-size: inherit !important;
        }
      `}</style>
      <div id="module-table-root">
        <div className="container mt-1">
          <ToastContainer />
          <Table data={modules} columns={columns} />
        </div>
      </div>
    </>
  );
};

export default ModuleTable;